import StatusChip from 'components/@extended/StatusChip';
import { CheckIcon, CircleAlert, CopyIcon, type SvgIconComponent, TriangleAlert } from 'components/icons';
import { Button } from 'components/ui/button';
import { CodeBlock, CodeBlockContent, CodeBlockLine } from 'components/ui/code-block';
import {
    TraceCollapsible,
    TraceCollapsibleContent,
    TraceCollapsibleTrigger,
    TraceLine,
    TraceTitle,
} from 'components/ui/trace';
import { CopiedStatus } from 'components/util/CopiedStatus';
import { FOCUS_RING_INSET } from 'layout/NewDesignLayout/focusRing';
import { ReactNode, useState } from 'react';
import { useIntl } from 'react-intl';
import type { AiToolCallHistoryItem, ToolCallDebugInfo } from 'types/aiChat';
import { cn } from 'utils/cn';
import { getIntl } from 'utils/getTranslations';
import { useCopied } from '../../hooks/useCopied';
import { parseChartSpecFromToolOutput } from '../../utils/chartSpec';
import ChartToolPart from '../message/ChartToolPart';
import { Spinner } from './Spinner';
import { partialLabel, toolIcon, toolName } from './toolSteps';

// A JSON line that opens with a key: the key is drawn in the darker ink.
const KEY_LINE = /^(\s*)("(?:[^"\\]|\\.)*")(:.*)$/;

const stringifyJson = (value: unknown) => {
    if (value == null || (typeof value === 'object' && Object.keys(value).length === 0)) {
        return null;
    }
    return JSON.stringify(value, null, 2);
};

// The locale writes the size and its unit.
const formatBytes = (bytes: number) => {
    const size = (value: number, unit: string) =>
        getIntl().formatNumber(value, { style: 'unit', unit, maximumFractionDigits: 1 });
    if (bytes < 1024) return size(bytes, 'byte');
    if (bytes < 1024 * 1024) return size(bytes / 1024, 'kilobyte');
    return size(bytes / (1024 * 1024), 'megabyte');
};

function CopyJsonButton({ json }: { json: string }) {
    const intl = useIntl();
    const [copied, markCopied] = useCopied();

    return (
        <>
            <Button
                variant="ghost"
                onClick={() => {
                    navigator.clipboard
                        .writeText(json)
                        .then(markCopied)
                        .catch((error) => console.error('Failed to copy tool payload', error));
                }}
                className="text-caption text-muted-foreground hover:bg-muted hover:text-foreground -my-1 h-7 gap-1.5 rounded-md ps-1.5 pe-2 font-medium"
            >
                <span className="t-icon-swap" data-state={copied ? 'b' : 'a'} aria-hidden="true">
                    <span className="t-icon" data-icon="a">
                        <CopyIcon className="size-3.5" />
                    </span>
                    <span className="t-icon text-success-ink" data-icon="b">
                        <CheckIcon className="size-3.5" />
                    </span>
                </span>
                {intl.formatMessage({ id: 'ai-chat.tool-call.action.copy-json' })}
            </Button>
            <CopiedStatus copied={copied} />
        </>
    );
}

function Section({ label, action, children }: { label: string; action?: ReactNode; children: ReactNode }) {
    return (
        <div className="flex min-w-0 flex-col gap-1.5">
            <div className="flex items-center justify-between gap-2">
                <p className="text-label text-foreground-tertiary m-0 uppercase">{label}</p>
                {action}
            </div>
            {children}
        </div>
    );
}

function JsonSection({ label, value }: { label: string; value: unknown }) {
    const json = stringifyJson(value);
    if (!json) {
        return null;
    }
    return (
        <Section label={label} action={<CopyJsonButton json={json} />}>
            <CodeBlock
                size="sm"
                className="bg-surface-subtle text-caption text-foreground-secondary rounded-md border-0"
            >
                <CodeBlockContent wrap aria-label={label} className="max-h-64 px-2.5 py-2">
                    {json.split('\n').map((line, index) => {
                        const key = KEY_LINE.exec(line);
                        return (
                            // The lines of one immutable JSON string: their order is their identity.
                            <CodeBlockLine key={index}>
                                {key ? (
                                    <>
                                        {key[1]}
                                        <span className="text-foreground">{key[2]}</span>
                                        {key[3]}
                                    </>
                                ) : (
                                    line
                                )}
                            </CodeBlockLine>
                        );
                    })}
                </CodeBlockContent>
            </CodeBlock>
        </Section>
    );
}

function Pair({ label, children }: { label: string; children: ReactNode }) {
    return (
        <div className="flex items-baseline gap-2">
            <dt className="text-caption text-foreground-tertiary">{label}</dt>
            <dd className="text-meta text-foreground m-0 font-semibold tabular-nums">{children}</dd>
        </div>
    );
}

function CardTile({ icon: Icon }: { icon: SvgIconComponent }) {
    return (
        <span className="bg-muted text-foreground-secondary grid size-8 shrink-0 place-items-center rounded-[9px]">
            <Icon className="size-4" />
        </span>
    );
}

function CardBody({ toolCall, debug }: { toolCall: AiToolCallHistoryItem; debug?: ToolCallDebugInfo }) {
    const intl = useIntl();
    const label = (id: string) => intl.formatMessage({ id: `ai-chat.tool-call.label.${id}` });
    const records = toolCall.summary?.count ?? debug?.recordCount;
    const chartSpec = toolCall.name === 'chart' ? parseChartSpecFromToolOutput(toolCall.output) : null;
    const hasSize = debug?.inputBytes != null || debug?.outputBytes != null;

    return (
        <div className="border-divider-soft flex flex-col gap-2.5 border-t p-3">
            {records != null || debug?.filter || hasSize ? (
                <dl className="m-0 flex flex-wrap gap-x-6 gap-y-1">
                    {records != null ? <Pair label={label('records')}>{records}</Pair> : null}
                    {debug?.filter ? <Pair label={label('filter')}>{debug.filter}</Pair> : null}
                    {hasSize ? (
                        <Pair label={label('size')}>
                            {debug?.inputBytes != null ? formatBytes(debug.inputBytes) : '–'} →{' '}
                            {debug?.outputBytes != null ? formatBytes(debug.outputBytes) : '–'}
                        </Pair>
                    ) : null}
                </dl>
            ) : null}
            <JsonSection label={label('arguments')} value={toolCall.input} />
            {debug?.requestedFields?.length ? (
                <Section label={label('fields')}>
                    <ul className="m-0 flex list-none flex-wrap gap-1.5 p-0">
                        {debug.requestedFields.map((field) => (
                            <li
                                key={field}
                                className="bg-neutral-tint text-caption text-foreground-secondary rounded-lg px-1.5 font-mono"
                            >
                                {field}
                            </li>
                        ))}
                    </ul>
                </Section>
            ) : null}
            {chartSpec ? (
                <Section label={label('output')}>
                    <ChartToolPart spec={chartSpec} />
                </Section>
            ) : (
                <JsonSection label={label('output')} value={toolCall.output} />
            )}
            {toolCall.errorText ? (
                <Section label={label('error')}>
                    <p className="text-meta text-danger-ink m-0 wrap-break-word">{toolCall.errorText}</p>
                </Section>
            ) : null}
        </div>
    );
}

function CardStatus({ toolCall }: { toolCall: AiToolCallHistoryItem }) {
    const intl = useIntl();
    const partial = partialLabel(intl, toolCall.summary);
    const chip = 'text-caption gap-1 px-1.5';
    if (toolCall.status === 'error') {
        return (
            <StatusChip
                type="error"
                className={chip}
                icon={<CircleAlert className="size-3" />}
                label={intl.formatMessage({ id: 'ai-chat.tool-call.status.error' })}
            />
        );
    }
    if (partial) {
        return (
            <StatusChip type="warning" className={chip} icon={<TriangleAlert className="size-3" />} label={partial} />
        );
    }
    return (
        <StatusChip
            type="success"
            className={chip}
            icon={<CheckIcon className="size-3" />}
            label={intl.formatMessage({ id: 'ai-chat.tool-call.status.success' })}
        />
    );
}

interface Props {
    toolCalls: AiToolCallHistoryItem[];
    isStreaming: boolean;
    toolDebug?: Record<string, ToolCallDebugInfo>;
}

/**
 * One card per tool call, for a user with the AI debug permission: the tool in plain words and its status while it
 * runs, then a card that opens on its counts, input, output and error, one open at a time.
 */
export default function ToolCallCards({ toolCalls, isStreaming, toolDebug }: Props) {
    const intl = useIntl();
    const [openId, setOpenId] = useState<string | null>(null);

    return (
        <ul className="m-0 flex list-none flex-col gap-2 p-0">
            {toolCalls.map((toolCall) => {
                const name = toolName(intl, toolCall.name);
                if (toolCall.status === 'pending') {
                    return (
                        <li
                            key={toolCall.id}
                            className="border-border bg-card new:border-0 new:shadow-card new:outline-hidden rounded-xl border px-3 py-2"
                        >
                            <TraceLine
                                status={isStreaming ? 'running' : 'pending'}
                                className="text-meta text-foreground w-full max-w-none gap-2.5"
                            >
                                <CardTile icon={toolIcon(toolCall.name)} />
                                <TraceTitle className="me-auto font-semibold">{name}</TraceTitle>
                                <StatusChip
                                    type="neutral"
                                    className="text-caption gap-1 px-1.5"
                                    icon={isStreaming ? <Spinner className="size-3" /> : undefined}
                                    label={intl.formatMessage({ id: 'ai-chat.tool-call.status.pending' })}
                                />
                            </TraceLine>
                        </li>
                    );
                }
                return (
                    <li
                        key={toolCall.id}
                        className="border-border bg-card new:border-0 new:shadow-card new:outline-hidden overflow-hidden rounded-xl border"
                    >
                        <TraceCollapsible
                            open={openId === toolCall.id}
                            onOpenChange={(open) => setOpenId(open ? toolCall.id : null)}
                        >
                            {/* The card clips an outside ring, so the trigger draws its ring inside. */}
                            <TraceCollapsibleTrigger
                                className={cn(
                                    'hover:bg-row-stripe text-foreground hover:text-foreground w-full max-w-none gap-2.5 rounded-none px-3 py-2 duration-150 focus-visible:ring-0',
                                    FOCUS_RING_INSET
                                )}
                            >
                                <CardTile icon={toolIcon(toolCall.name)} />
                                <span className="flex min-w-0 flex-1 flex-wrap items-center gap-x-3 gap-y-1">
                                    <span className="text-meta text-foreground me-auto min-w-0 font-semibold">
                                        {name}
                                    </span>
                                    <CardStatus toolCall={toolCall} />
                                </span>
                            </TraceCollapsibleTrigger>
                            <TraceCollapsibleContent className="m-0 border-l-0 p-0">
                                <CardBody toolCall={toolCall} debug={toolDebug?.[toolCall.id]} />
                            </TraceCollapsibleContent>
                        </TraceCollapsible>
                    </li>
                );
            })}
        </ul>
    );
}
